/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react'
import { Button, Col, Form, Row, Tab, Tabs } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { getFinalValuesFromTaskParameterFunction } from '../../../config'
import { cloneChildComponents } from '../../../utils/helpers'
import { getEffectiveGwsLicenseSelections } from '../../../../../../util/employeeLicenses.util'

function ManageLicenses(props) {
  const { children, ...rest } = props
  const {
    show,
    action,
    taskParameters,
    setTaskParameters,
    allLicensedEmployees,
    onActionSubmit,
    selectedEmployee,
    setDisableSubmit,
    setActionData,
    actionData,
    setCompletedActions,
    setSubmitButtonLangId,
  } = rest

  // Local state
  const [licenses, setLicenses] = useState([]) // List of all unique licenses from all employees
  const [licensesToAdd, setLicensesToAdd] = useState([]) // List of licenses that will be getting added to an employee
  const [licensesToRemove, setLicensesToRemove] = useState([]) // List of licenses that will be getting removed from an employee
  const [employeeAssignedLicenses, setEmployeeAssignedLicenses] = useState([]) // List of all licenses currently assingned to an employee
  const [activeKey, setActiveKey] = useState('manage-licenses') // The current tab of the modal

  // Success action ids
  const LICENSES_ASSIGNED = 'ENGAGE.LICENSES_ASSIGNED'
  const LICENSES_REMOVED = 'ENGAGE.LICENSES_REMOVED'

  // Handles initial render of component and refreshes when employee license data arrives
  useEffect(() => {
    if (show) {
      // eslint-disable-next-line no-use-before-define
      getAllLicenses()
    }
  }, [show, selectedEmployee, allLicensedEmployees]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handles firing off the task once completed tabs and all data is set
  useEffect(() => {
    // Submit the task if actionData has either licenseIdsToAdd or licenseIdsToRemove as a key. This is the final state setter and is used as a reference for when to fire off the task
    if (
      // eslint-disable-next-line no-prototype-builtins
      actionData?.hasOwnProperty('licenseIdsToAdd') ||
      actionData?.hasOwnProperty('licenseIdsToRemove') // eslint-disable-line no-prototype-builtins
    ) {
      // eslint-disable-next-line no-use-before-define
      submitTheTask()
    }
  }, [actionData]) // eslint-disable-line react-hooks/exhaustive-deps

  // Track current employee licenses for change detection
  const currentEmployeeLicenses = useMemo(() => {
    if (!selectedEmployee?.licenseType?.length) return ''
    return selectedEmployee.licenseType
      .map((license) => license.id)
      .sort()
      .join(',')
  }, [selectedEmployee])

  useEffect(() => {
    const assignedLicenses = []

    if (selectedEmployee?.licenseType?.length) {
      // eslint-disable-next-line no-restricted-syntax
      for (const assignedLicense of selectedEmployee.licenseType) {
        assignedLicenses.push(assignedLicense)
      }
    }

    setEmployeeAssignedLicenses(assignedLicenses)
  }, [selectedEmployee]) // eslint-disable-line react-hooks/exhaustive-deps

  const { licensesToAdd: effectiveLicensesToAdd, licensesToRemove: effectiveLicensesToRemove } = useMemo(
    () =>
      getEffectiveGwsLicenseSelections({
        licensesToAdd,
        licensesToRemove,
        currentlyAssigned: employeeAssignedLicenses,
        catalog: licenses,
      }),
    [licensesToAdd, licensesToRemove, employeeAssignedLicenses, licenses],
  )

  // Reset selections when licenses change
  useEffect(() => {
    if (show) {
      setActiveKey('manage-licenses')
      setLicensesToAdd([])
      setLicensesToRemove([])
    }
  }, [show, currentEmployeeLicenses])

  // Handles effective add/remove change -> sets licensesAssigned in the actionData to be used in the success body
  useEffect(() => {
    const updatedActionData = {}
    if (effectiveLicensesToAdd?.length) {
      updatedActionData.licensesAssigned = effectiveLicensesToAdd
    }
    if (effectiveLicensesToRemove?.length) {
      updatedActionData.licensesRemoved = effectiveLicensesToRemove
    }
    setActionData({ ...actionData, ...updatedActionData })
  }, [effectiveLicensesToAdd, effectiveLicensesToRemove]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handles disabling submit button in the footer child component
  useEffect(() => {
    const hasNoEffectiveChanges = effectiveLicensesToAdd.length === 0 && effectiveLicensesToRemove.length === 0
    if (activeKey === 'manage-licenses' && hasNoEffectiveChanges) {
      setDisableSubmit(true)
    } else {
      setDisableSubmit(false)
    }
  }, [activeKey, actionData, effectiveLicensesToAdd, effectiveLicensesToRemove]) // eslint-disable-line react-hooks/exhaustive-deps

  // Retrieves all GWS licenses from employees and forms a hashmap of licenseId: license object.
  // Prefer selectedEmployee licenseType — allLicensedEmployees can lag or omit licenses.
  const getAllLicenses = async () => {
    const licenseMap = new Map() // {key: licenseId, value: licenseObject}

    const employeesForCatalog = [...(allLicensedEmployees || [])]
    if (selectedEmployee) {
      const selectedEmployeeId = selectedEmployee?.employeeId ?? selectedEmployee?.id
      const existingIndex = employeesForCatalog.findIndex(
        (employee) => (employee?.employeeId ?? employee?.id) === selectedEmployeeId,
      )

      if (existingIndex >= 0) {
        const existingEmployee = employeesForCatalog[existingIndex]
        employeesForCatalog[existingIndex] = {
          ...existingEmployee,
          ...selectedEmployee,
          licenseType: selectedEmployee.licenseType?.length
            ? selectedEmployee.licenseType
            : existingEmployee.licenseType || [],
        }
      } else {
        employeesForCatalog.push(selectedEmployee)
      }
    }

    // Get the base (default) GWS license from any employees
    // eslint-disable-next-line no-restricted-syntax
    for (const employee of employeesForCatalog) {
      // eslint-disable-next-line no-restricted-syntax
      for (const license of employee?.licenseType || []) {
        if (licenseMap.has(license.id)) {
          continue // eslint-disable-line no-continue
        } else {
          // Add the license to the map
          licenseMap.set(license.id, license)
        }
      }
    }

    setLicenses(Array.from(licenseMap, ([, value]) => ({ ...value }))) // Convert map to array of objects
  }

  // Updates the licensesToAdd and licensesToRemove states to determine which licenses will be assigned/unassigned
  const updateLicenses = (licenseId, checked) => {
    const licensesToAssign = [...licensesToAdd]
    const licensesToUnassign = [...licensesToRemove]
    let indexToRemove

    // License was selected
    if (licenseId && checked) {
      // Add the license to licensesToAdd IFF it is not currently assigned to the employee
      if (!employeeAssignedLicenses.some((license) => license?.id === licenseId)) {
        licensesToAssign.push(licenses.find((license) => license?.id === licenseId))
      }

      // It may have previously been added to licensesToRemove, so check if it needs to be removed
      indexToRemove = licensesToUnassign.findIndex((license) => license?.id === licenseId)
      if (indexToRemove !== undefined) {
        // Remove it from licensesToRemove
        licensesToUnassign.splice(indexToRemove, 1)
      }
      // License was unchecked
    } else if (licenseId && !checked) {
      // Add the license to licensesToRemove IFF it is currently assigned to the employee
      if (employeeAssignedLicenses.some((license) => license?.id === licenseId)) {
        // The employee did previously have it checked, so add it to licensesToRemove
        licensesToUnassign.push(licenses.find((license) => license?.id === licenseId))
      }
      // It may have previously been added to licenesToAssign, so check if it needs to be removed
      indexToRemove = licensesToAssign.findIndex((license) => license.id === licenseId)
      if (indexToRemove !== -1) {
        // Remove it from licensesToAssign
        licensesToAssign.splice(indexToRemove, 1)
      }
    }

    setLicensesToAdd(licensesToAssign)
    setLicensesToRemove(licensesToUnassign)
  }

  // onSubmit handler passed down to footer
  const next = () => {
    if (activeKey === 'manage-licenses') {
      setSubmitButtonLangId('ADMINISTRATION.UPDATE_LICENSES')
      setActiveKey('review')
    } else {
      const newCompletedActions = []
      const newActionData = {}

      // Set licenseIdsToAdd in actionData for task parameters, as well as the completed actionId
      if (effectiveLicensesToAdd?.length) {
        newCompletedActions.push(LICENSES_ASSIGNED)
        newActionData.licenseIdsToAdd = effectiveLicensesToAdd.map((license) => license.id)
      }

      // Set licenseIdsToRemove in actionData for task parameters, as well as the completed actionId
      if (effectiveLicensesToRemove?.length) {
        newCompletedActions.push(LICENSES_REMOVED)
        newActionData.licenseIdsToRemove = effectiveLicensesToRemove.map((license) => license.id)
      }

      // Set the new completed actions and action data
      setCompletedActions((alreadyCompletedActions) => [...alreadyCompletedActions, ...newCompletedActions])
      setActionData({ ...actionData, ...newActionData })
      // Note: This will trigger the final useEffect that handles firing off the task
    }
  }

  // Fires off task to integration manager
  const submitTheTask = () => {
    let finalTaskParameters = taskParameters

    // Form task parameter fields for taskParameters callback
    const taskParameterFields = {
      selectedEmployee,
      actionData,
    }

    // Task parameters should be a callback function here that was set in Data Loader
    if (typeof finalTaskParameters === 'function') {
      try {
        finalTaskParameters = getFinalValuesFromTaskParameterFunction(action, finalTaskParameters, taskParameterFields)
      } catch (error) {
        console.error(error)
      }
      setTaskParameters(finalTaskParameters)
    } else {
      console.error('Error - taskParameters should be a function')
    }

    // Submit the task
    onActionSubmit(action, finalTaskParameters)
  }

  return (
    <PerfectScrollbar options={{ wheelSpeed: 2, wheelPropagation: false }} className="paginator-margin-bottom">
      <Form>
        <Tabs activeKey={activeKey} onSelect={(key) => setActiveKey(key)} className="nav-fill engage-tabs">
          <Tab
            eventKey="manage-licenses"
            title={
              <p>
                <FormattedMessage id="ENGAGE.MANAGE" />
              </p>
            }
          >
            <Row>
              <Col sm="12" md="12" lg="12">
                <p className="font-bolder font-lg mb-4">
                  <FormattedMessage id="ENGAGE.MANAGE_LICENSES_TITLE" />
                </p>

                <Form.Group id="licenses" className="mb-0 ms-4">
                  {licenses.map((license) => {
                    const licenseIsCurrentlyAssigned = employeeAssignedLicenses.some(
                      (assignedLicense) => assignedLicense.id === license?.id,
                    )

                    return (
                      <Form.Check
                        custom
                        type="checkbox"
                        id={license?.id}
                        key={license?.id}
                        defaultChecked={licenseIsCurrentlyAssigned}
                        label={
                          <>
                            <span className="font-bolder">{license?.displayName}</span>
                            {licenseIsCurrentlyAssigned ? (
                              <p className="font-color-6" style={{ marginBottom: '0px' }}>
                                <FormattedMessage id="GENERAL.ASSIGNED" />
                              </p>
                            ) : (
                              <p className="font-color-6" style={{ marginBottom: '0px' }}>
                                <FormattedMessage id="ENGAGE.UNASSIGNED" />
                              </p>
                            )}
                          </>
                        }
                        onClick={(e) => {
                          updateLicenses(e.target.id, e.target.checked)
                        }}
                      />
                    )
                  })}
                </Form.Group>
              </Col>
            </Row>
          </Tab>
          <Tab
            eventKey="review"
            title={
              <p>
                <FormattedMessage id="ENGAGE.REVIEW_AND_FINISH" />
              </p>
            }
            disabled={activeKey !== 'review'}
          >
            <Row>
              <Col sm="12" md="12" lg="12">
                {effectiveLicensesToAdd?.length >= 1 && (
                  <p className="font-bolder font-lg mb-2">
                    <FormattedMessage id="ENGAGE.LICENSES_TO_BE_ASSIGNED" />
                  </p>
                )}
                {effectiveLicensesToAdd.map((license, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <p key={index} className="mb-0">
                    {license.displayName}
                  </p>
                ))}
                {effectiveLicensesToRemove?.length >= 1 && (
                  <p className="font-bolder font-lg mb-2">
                    <FormattedMessage id="ENGAGE.LICENSES_TO_BE_REMOVED" />
                  </p>
                )}
                {effectiveLicensesToRemove.map((license, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <p key={index} className="mb-0">
                    {license.displayName}
                  </p>
                ))}
                <Button variant="link" className="mb-4 ps-0 pe-0" onClick={() => setActiveKey('manage-licenses')}>
                  <span>
                    <FormattedMessage id="GENERAL.EDIT" />
                  </span>
                </Button>
              </Col>
            </Row>
          </Tab>
        </Tabs>

        {/* Footer Child */}
        {cloneChildComponents(
          {
            ...rest,
            handleSubmit: next,
          },
          children,
        )}
      </Form>
    </PerfectScrollbar>
  )
}

export default ManageLicenses
